import { createServerFn } from '@tanstack/react-start'
import z from 'zod'
import { redirect } from '@tanstack/react-router'
import { ID, type Models } from 'node-appwrite'
import {
  deleteCookie,
  getCookie,
  setCookie,
  setResponseStatus,
} from '@tanstack/react-start/server'

export const getAppwriteSessionFn = createServerFn({ method: 'GET' }).handler(
  async () => getAppwriteSession(),
)

function getAppwriteConfig() {
  const endpoint = process.env.APPWRITE_ENDPOINT
  const projectId = process.env.APPWRITE_PROJECT_ID
  const apiKey = process.env.APPWRITE_API_KEY

  if (!endpoint || !projectId || !apiKey) {
    throw new Error('Missing Appwrite environment variables')
  }

  return { endpoint, projectId, apiKey }
}

async function appwriteFetch<T>(
  path: string,
  init: RequestInit & { session?: string } = {},
) {
  const { endpoint, projectId, apiKey } = getAppwriteConfig()
  const headers = new Headers(init.headers)

  headers.set('content-type', 'application/json')
  headers.set('x-appwrite-project', projectId)
  headers.set('x-appwrite-key', apiKey)

  if (init.session) {
    headers.set('x-appwrite-session', init.session)
  }

  const response = await fetch(`${endpoint}${path}`, {
    ...init,
    headers,
  })
  const body = await response.json().catch(() => null)

  if (!response.ok) {
    throw new Error(body?.message || `Appwrite request failed: ${response.status}`)
  }

  return body as T
}

type AppwriteSession = { $id: string; secret: string }
type AppwriteUser = Models.User<Models.Preferences>

function getAppwriteSession() {
  return getCookie(`appwrite-session-secret`) ?? null
}

export const setAppwriteSessionCookiesFn = createServerFn({ method: 'POST' })
  .inputValidator(z.object({ id: z.string(), secret: z.string() }))
  .handler(async ({ data }) => {
    setAppwriteSessionCookies(data)
  })

function setAppwriteSessionCookies(data: { id: string; secret: string }) {
  setCookie(`appwrite-session-secret`, data.secret, {
    httpOnly: true,
    secure: true,
    sameSite: 'none',
  })

  setCookie(`appwrite-session-id`, data.id, {
    httpOnly: true,
    secure: true,
    sameSite: 'none',
  })
}

const signUpInSchema = z.object({
  email: z.email('Please enter a valid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  redirect: z.string().optional(),
})

export const signUpFn = createServerFn({ method: 'POST' })
  .inputValidator(signUpInSchema)
  .handler(async ({ data }) => {
    const { email, password, redirect: redirectUrl } = data

    try {
      await appwriteFetch('/account', {
        method: 'POST',
        body: JSON.stringify({ userId: ID.unique(), email, password }),
      })
      const session = await appwriteFetch<AppwriteSession>(
        '/account/sessions/email',
        {
          method: 'POST',
          body: JSON.stringify({ email, password }),
        },
      )
      setAppwriteSessionCookies({ id: session.$id, secret: session.secret })
    } catch (_error) {
      const error = _error as Error
      setResponseStatus(400)
      throw new Error(error.message || 'Failed to create account')
    }

    if (redirectUrl) {
      throw redirect({ to: redirectUrl })
    } else {
      throw redirect({ to: '/' })
    }
  })

export const signInFn = createServerFn({ method: 'POST' })
  .inputValidator(signUpInSchema)
  .handler(async ({ data }) => {
    const { email, password, redirect: redirectUrl } = data

    try {
      const session = await appwriteFetch<AppwriteSession>(
        '/account/sessions/email',
        {
          method: 'POST',
          body: JSON.stringify({ email, password }),
        },
      )
      setAppwriteSessionCookies({ id: session.$id, secret: session.secret })
    } catch (_error) {
      const error = _error as Error
      setResponseStatus(401)
      throw new Error(error.message || 'Invalid credentials')
    }

    if (redirectUrl) {
      throw redirect({ to: redirectUrl })
    } else {
      throw redirect({ to: '/' })
    }
  })

export const signOutFn = createServerFn({ method: 'POST' }).handler(
  async () => {
    deleteCookie(`appwrite-session-secret`)
    deleteCookie(`appwrite-session-id`)
  },
)

export const authMiddleware = createServerFn({ method: 'GET' }).handler(
  async () => {
    const currentUser = await getCurrentUserFromSession()

    return {
      currentUser,
    }
  },
)

export const getCurrentUser = createServerFn({ method: 'GET' }).handler(
  async () => getCurrentUserFromSession(),
)

async function getCurrentUserFromSession(): Promise<AppwriteUser | null> {
  const session = getAppwriteSession()

  if (!session) {
    return null
  }

  return appwriteFetch<AppwriteUser>('/account', { method: 'GET', session })
}
